/**
 * Garage Filter Integration for Shopify Collection Pages
 *
 * This script automatically applies collection filters based on ALL vehicles
 * saved in the customer's garage.
 *
 * Installation:
 * 1. Add this script to your theme's assets folder
 * 2. Include it on collection pages (collection.liquid or collection-template.liquid)
 */

(function() {
  'use strict';

  console.log('[Garage Filter] Script loaded - DEBUG MODE');

  // Configuration
  const CONFIG = {
    API_BASE_URL: '/apps/garage',
    APPLY_ON_PAGES: ['/collections'], // Pages where filters should apply
    FILTER_SELECTORS: {
      // Shopify Dawn theme filter selectors
      make: 'input[name="filter.p.m.custom.make"]',
      year: 'input[name="filter.p.m.custom.year"]',
      model: 'input[name="filter.p.m.custom.model"]',
      type: 'input[name="filter.p.m.custom.type"]',

      // Form to submit after filters are set
      filterForm: 'form[id*="filter"], form.collection-filters, form.facets-form, form.FacetsWrapperDesktop'
    },
    DEBUG: true, // Set to false in production
    DEBUG_NO_REDIRECT: false // Set to true to prevent redirect and see console output
  };

  // Utility: Log messages if debug is enabled
  function log(...args) {
    if (CONFIG.DEBUG) {
      console.log('[Garage Filter]', ...args);
    }
  }

  // Check if we're on a collection page
  function shouldApplyFilters() {
    const currentPath = window.location.pathname;
    return CONFIG.APPLY_ON_PAGES.some(page => currentPath.includes(page));
  }

  // Get customer ID from Shopify
  function getCustomerId() {
    // Try to get from Shopify's customer object
    if (window.Shopify && window.Shopify.customer) {
      return `gid://shopify/Customer/${window.Shopify.customer.id}`;
    }

    // Try from meta tag (if you've added one)
    const metaTag = document.querySelector('meta[name="shopify-customer-id"]');
    if (metaTag) {
      return metaTag.content;
    }

    return null;
  }

  // Fetch garage data from backend
  async function fetchGarageFilters(customerId) {
    try {
      log('Fetching garage filters for customer:', customerId);

      const response = await fetch(
        `${CONFIG.API_BASE_URL}/customer/vehicles/filters?customerId=${encodeURIComponent(customerId)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      log('Garage filter data:', data);
      console.log('[Garage Filter] Raw API response:', JSON.stringify(data, null, 2));
      console.log('[Garage Filter] Vehicles GIDs:', data.vehicles);
      console.log('[Garage Filter] Filters in response:', data.filters);

      // Log full vehicle details to see all fields
      if (data.vehicleDetails && data.vehicleDetails.length > 0) {
        console.log('[Garage Filter] === FULL VEHICLE DETAILS ===');
        data.vehicleDetails.forEach((vehicle, i) => {
          console.log(`[Garage Filter] Vehicle ${i + 1}:`, JSON.stringify(vehicle, null, 2));
          console.log(`[Garage Filter] Vehicle ${i + 1} keys:`, Object.keys(vehicle));
        });
        console.log('[Garage Filter] ============================');
      } else {
        console.log('[Garage Filter] No vehicleDetails in response');
      }

      return data;
    } catch (error) {
      log('Error fetching garage filters:', error);
      return null;
    }
  }

  // Apply ALL filters from the garage (multiple makes, years, models)
  function applyAllFilters(filterData) {
    if (!filterData || !filterData.filters) {
      log('No filters to apply');
      return false;
    }

    const { makes, years, models, types } = filterData.filters;
    log('Applying all filters:', { makes, years, models, types });

    let filtersApplied = false;

    // Apply all makes
    if (makes && makes.length > 0) {
      for (const make of makes) {
        if (selectFilter(CONFIG.FILTER_SELECTORS.make, make)) {
          filtersApplied = true;
        }
      }
    }

    // Apply all years
    if (years && years.length > 0) {
      for (const year of years) {
        if (selectFilter(CONFIG.FILTER_SELECTORS.year, year.toString())) {
          filtersApplied = true;
        }
      }
    }

    // Apply all models
    if (models && models.length > 0) {
      for (const model of models) {
        if (selectFilter(CONFIG.FILTER_SELECTORS.model, model)) {
          filtersApplied = true;
        }
      }
    }

    // Apply all types (car, truck, all)
    if (types && types.length > 0) {
      for (const type of types) {
        if (selectFilter(CONFIG.FILTER_SELECTORS.type, type)) {
          filtersApplied = true;
        }
      }
    }

    return filtersApplied;
  }

  // Select a filter checkbox by value
  function selectFilter(selector, value) {
    try {
      const inputs = document.querySelectorAll(selector);

      for (const input of inputs) {
        // Check if the input's value matches (case-insensitive)
        if (input.value.toLowerCase() === value.toString().toLowerCase()) {
          if (!input.checked) {
            input.checked = true;
            log(`Selected filter: ${selector} = ${value}`);

            // Trigger change event
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }
      }
    } catch (error) {
      log('Error selecting filter:', error);
    }

    return false;
  }

  // URL-based filter application for ALL garage vehicles
  function applyFiltersViaURL(filterData) {
    if (!filterData || !filterData.filters) {
      return false;
    }

    const { makes, years, models, types } = filterData.filters;

    // DEBUG: Log filter data before redirect
    console.log('=== GARAGE FILTER DEBUG (before redirect) ===');
    console.log('Makes:', makes);
    console.log('Years:', years);
    console.log('Models:', models);
    console.log('Types:', types);
    console.log('Full filterData:', JSON.stringify(filterData, null, 2));
    console.log('==============================================');

    const url = new URL(window.location.href);
    let hasFilters = false;

    // Clear existing garage-related filters first
    url.searchParams.delete('filter.p.m.custom.make');
    url.searchParams.delete('filter.p.m.custom.year');
    url.searchParams.delete('filter.p.m.custom.model');
    url.searchParams.delete('filter.p.m.custom.type');

    // Add all makes as separate parameters
    if (makes && makes.length > 0) {
      for (const make of makes) {
        url.searchParams.append('filter.p.m.custom.make', make);
        hasFilters = true;
      }
    }

    // Add all years as separate parameters
    if (years && years.length > 0) {
      for (const year of years) {
        url.searchParams.append('filter.p.m.custom.year', year.toString());
        hasFilters = true;
      }
    }

    // Add all models as separate parameters
    if (models && models.length > 0) {
      for (const model of models) {
        url.searchParams.append('filter.p.m.custom.model', model);
        hasFilters = true;
      }
    }

    // Add all types as separate parameters (car, truck, all)
    if (types && types.length > 0) {
      for (const type of types) {
        url.searchParams.append('filter.p.m.custom.type', type);
        hasFilters = true;
      }
    }

    // Only reload if filters were added and URL changed
    if (hasFilters && url.toString() !== window.location.href) {
      log('Applying filters via URL:', url.toString());
      window.location.href = url.toString();
      return true;
    }

    return false;
  }

  // Main initialization
  async function init(forceReload = false) {
    log('Garage filter integration initializing...', forceReload ? '(forced reload)' : '');

    // Check if we should apply filters on this page
    if (!shouldApplyFilters()) {
      log('Not on a collection page, skipping');
      return;
    }

    // Check if filters are already applied (to avoid re-applying on page load)
    // Skip this check if forceReload is true (triggered by garage update)
    if (!forceReload && window.location.search.includes('filter.p.m.custom.')) {
      log('Garage filters already in URL, skipping auto-apply');
      return;
    }

    // Get customer ID
    const customerId = getCustomerId();

    if (!customerId) {
      log('Customer not logged in, skipping');
      return;
    }

    // Fetch garage filter data
    const filterData = await fetchGarageFilters(customerId);

    if (!filterData || filterData.vehicles.length === 0) {
      log('No vehicles in garage, skipping');
      // If force reload and no vehicles, clear the filters
      if (forceReload && window.location.search.includes('filter.p.m.custom.')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('filter.p.m.custom.make');
        url.searchParams.delete('filter.p.m.custom.year');
        url.searchParams.delete('filter.p.m.custom.model');
        url.searchParams.delete('filter.p.m.custom.type');
        window.location.href = url.toString();
      }
      return;
    }

    log(`Customer has ${filterData.vehicles.length} vehicle(s) in garage`);
    log('All filters:', filterData.filters);

    // Wait a moment for filters to be fully rendered
    await new Promise(resolve => setTimeout(resolve, 500));

    // Debug: Log available filter inputs
    const makeInputs = document.querySelectorAll(CONFIG.FILTER_SELECTORS.make);
    const yearInputs = document.querySelectorAll(CONFIG.FILTER_SELECTORS.year);
    const typeInputs = document.querySelectorAll(CONFIG.FILTER_SELECTORS.type);
    log(`Found ${makeInputs.length} make inputs, ${yearInputs.length} year inputs, ${typeInputs.length} type inputs`);

    if (makeInputs.length > 0) {
      log('Available makes:', Array.from(makeInputs).map(i => i.value));
    }

    // Try to apply filters via URL (most reliable for multiple values)
    log('Applying filters via URL for all garage vehicles');
    applyFiltersViaURL(filterData);
  }

  // Listen for garage updates
  document.addEventListener('garage:updated', (event) => {
    log('Garage updated event received:', event.detail);
    // Re-apply filters with force reload
    init(true);
  });

  // Run when DOM is ready, with a small delay for dynamic content
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(() => init(false), 100));
  } else {
    setTimeout(() => init(false), 100);
  }

})();
